<script lang="ts">
  import InspectorField from '../InspectorField.svelte';
  import ConfirmDialog from '../../ui/ConfirmDialog.svelte';
  import { updateDinModule, deleteDinModule, getErrorMessage } from '$lib/api';
  import { clearEntity, selectEntity } from '$lib/router';
  import { rcusQuery } from '$lib/queries/rcus';
  import { dinModulesForUnitQuery } from '$lib/queries/din_modules';
  import { invalidateAfterDinModuleCreate } from '$lib/state/invalidation';
  import { router } from '$lib/router';
  import { DIN_MODULE_TYPES, LEGACY_DIN_MODULE_TYPES } from '$lib/hardware';
  import type { DinModuleRequest } from '$lib/types/din_module';

  let { id }: { id: number } = $props();

  const EDITABLE_MODULE_TYPES = { ...DIN_MODULE_TYPES, ...LEGACY_DIN_MODULE_TYPES };

  const rcus = rcusQuery();
  const dinModules = dinModulesForUnitQuery();

  let mod = $derived((dinModules.data ?? []).find((m) => m.id === id));
  let parentRcu = $derived(mod ? (rcus.data ?? []).find((r) => r.id === mod.rcu) : null);

  let showDeleteConfirm = $state(false);
  let selectError = $state('');

  async function saveAddress(value: string) {
    if (!mod) return;
    const address = Number(value);
    if (!address) throw new Error('Address is required');
    const request: DinModuleRequest = {
      address,
      module_type: mod.module_type,
      ...(mod.dali_bus_count != null ? { dali_bus_count: mod.dali_bus_count } : {})
    };
    await updateDinModule(id, request);
    invalidateAfterDinModuleCreate(router.unitId!, mod.rcu);
  }

  async function saveModuleType(event: Event) {
    if (!mod) return;
    const module_type = (event.currentTarget as HTMLSelectElement)
      .value as DinModuleRequest['module_type'];
    if (!module_type || module_type === mod.module_type) return;
    selectError = '';
    try {
      const request: DinModuleRequest = {
        address: mod.address,
        module_type,
        ...(mod.dali_bus_count != null ? { dali_bus_count: mod.dali_bus_count } : {})
      };
      await updateDinModule(id, request);
      invalidateAfterDinModuleCreate(router.unitId!, mod.rcu);
    } catch (e) {
      selectError = getErrorMessage(e) || 'Failed to update module type';
    }
  }

  async function handleDelete() {
    showDeleteConfirm = false;
    if (!mod) return;
    const rcuId = mod.rcu;
    await deleteDinModule(mod.id);
    invalidateAfterDinModuleCreate(router.unitId!, rcuId);
    clearEntity();
  }
</script>

{#if mod}
  <div class="px-3 py-3 space-y-4">
    <div class="space-y-2">
      <div class="space-y-1">
        <label class="block text-2xs font-medium text-text-muted uppercase tracking-wide">
          Module type
        </label>
        <select
          class="w-full px-2 py-1.5 text-sm border border-border-strong rounded bg-bg-canvas text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-primary"
          value={mod.module_type}
          onchange={saveModuleType}
        >
          {#each Object.values(EDITABLE_MODULE_TYPES) as spec (spec.key)}
            <option value={spec.key}>{spec.label}</option>
          {/each}
        </select>
        {#if selectError}
          <p class="text-2xs text-state-error">{selectError}</p>
        {/if}
      </div>

      <InspectorField
        label="Address"
        type="number"
        value={String(mod.address ?? '')}
        mono
        onSave={saveAddress}
      />

      <div class="space-y-1">
        <p class="text-2xs font-medium text-text-muted uppercase tracking-wide">Parent RCU</p>
        {#if parentRcu}
          <button
            type="button"
            class="text-sm text-text-primary hover:text-state-info transition-colors duration-100"
            onclick={() => selectEntity('rcu', parentRcu.id)}
          >
            {parentRcu.name}
          </button>
        {:else}
          <p class="text-sm text-text-muted">—</p>
        {/if}
      </div>
    </div>

    <div class="pt-4 border-t border-border-default space-y-2">
      <p class="text-2xs font-medium text-text-muted uppercase tracking-wide">Danger zone</p>
      <button
        type="button"
        class="w-full px-2 py-1.5 text-sm text-state-error border border-state-error/30 rounded hover:bg-state-error/5 transition-colors duration-100"
        onclick={() => (showDeleteConfirm = true)}
      >
        Delete DIN module
      </button>
    </div>
  </div>
{:else}
  <div class="px-3 py-3 text-sm text-text-muted">DIN module not found</div>
{/if}

<ConfirmDialog
  bind:open={showDeleteConfirm}
  title="Delete DIN module"
  message="Delete DIN module at address {mod?.address}? Channel assignments will be removed."
  confirmText="Delete"
  onconfirm={handleDelete}
  oncancel={() => (showDeleteConfirm = false)}
/>
